import React, { useState } from "react";

import {
	Badge,
	Button,
	ConfirmModal,
	Picto,
	SectionCard,
} from "@amphore-dev/amphore-lib";
import { format } from "date-fns";
import { t } from "i18next";
import { toast } from "react-toastify";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import SessionsService from "@/services/SessionsService";

import { meKeys } from "@/constants/CQueryKeys";

import { TSession } from "@/types";

import { errorToast, promiseToast } from "@/modules/index";
import { cn, describeUserAgent } from "@/utils";

interface ISessionsCardProps {
	className?: string;
}

/** "Chrome · macOS", or a fallback when the browser says too little. */
export const sessionLabel = (session: TSession): string => {
	const { browser, system } = describeUserAgent(session.userAgent);
	const label = [browser, system].filter(Boolean).join(" · ");
	return label || t("account.sessions.unknown");
};

const formatDate = (iso: string) => format(new Date(iso), "dd/MM/yyyy HH:mm");

/**
 * The account's signed-in devices: where and when each one was last seen,
 * signed out one by one or all but this one. This device is listed but
 * not offered there - the usual logout is how it signs out.
 */
export const SessionsCard: React.FC<ISessionsCardProps> = ({ className }) => {
	const queryClient = useQueryClient();
	const [signingOut, setSigningOut] = useState<TSession | null>(null);
	const [signingOutOthers, setSigningOutOthers] = useState(false);

	const { data: sessions = [] } = useQuery({
		queryKey: meKeys.sessions,
		queryFn: SessionsService.list,
	});
	const refresh = () =>
		queryClient.invalidateQueries({ queryKey: meKeys.sessions });

	const revoke = useMutation({
		mutationFn: (id: number) => SessionsService.revoke(id),
		onSettled: () => {
			setSigningOut(null);
			void refresh();
		},
	});
	const revokeOthers = useMutation({
		mutationFn: SessionsService.revokeOthers,
		onSettled: () => {
			setSigningOutOthers(false);
			void refresh();
		},
	});

	const others = sessions.filter((session) => !session.current);

	return (
		<SectionCard
			title={t("account.sessions.title")}
			className={cn([className])}
			actions={
				others.length > 0 ? (
					<Button
						size="sm"
						variant="outline"
						color="danger"
						onClick={() => setSigningOutOthers(true)}
						data-testid="sessions-sign-out-others"
					>
						{t("account.sessions.signOutOthers")}
					</Button>
				) : undefined
			}
		>
			<div className="flex flex-col gap-3">
				<p className="text-sm text-neutral-500">
					{t("account.sessions.intro")}
				</p>
				<ul className="flex flex-col divide-y divide-neutral-100">
					{sessions.map((session) => (
						<li
							key={session.id}
							className="flex flex-wrap items-center justify-between gap-2 py-2"
							data-testid={`session-${session.id}`}
						>
							<span className="flex min-w-0 items-center gap-3">
								<Picto
									icon={
										describeUserAgent(session.userAgent)
											.device
									}
									className="w-5 shrink-0 text-neutral-500"
								/>
								<span className="flex min-w-0 flex-col">
									<span className="flex items-center gap-2 font-medium">
										{sessionLabel(session)}
										{session.current && (
											<Badge
												size="sm"
												variant="tint"
												color="success"
											>
												{t("account.sessions.current")}
											</Badge>
										)}
									</span>
									<span className="text-xs text-neutral-500">
										{t("account.sessions.dates", {
											signedIn: formatDate(
												session.signedInAt
											),
											lastActive: formatDate(
												session.lastActiveAt
											),
										})}
										{session.ip &&
											` · ${t("account.sessions.ip", { ip: session.ip })}`}
									</span>
								</span>
							</span>
							{!session.current && (
								<Button
									size="sm"
									variant="ghost"
									color="danger"
									onClick={() => setSigningOut(session)}
								>
									{t("account.sessions.signOut")}
								</Button>
							)}
						</li>
					))}
				</ul>
				{sessions.length > 0 && others.length === 0 && (
					<p className="text-sm text-neutral-500">
						{t("account.sessions.empty")}
					</p>
				)}
			</div>

			<ConfirmModal
				open={!!signingOut}
				onClose={() => setSigningOut(null)}
				onConfirm={async () => {
					if (!signingOut) return;
					// The toast says how it went; the modal just waits for it.
					await promiseToast(revoke.mutateAsync(signingOut.id), {
						success: t("account.sessions.signedOut"),
					}).catch(() => undefined);
				}}
				title={t("account.sessions.signOutTitle")}
				confirmText={t("account.sessions.signOut")}
				confirmColor="danger"
			>
				{signingOut &&
					t("account.sessions.signOutText", {
						device: sessionLabel(signingOut),
					})}
			</ConfirmModal>

			<ConfirmModal
				open={signingOutOthers}
				onClose={() => setSigningOutOthers(false)}
				onConfirm={async () => {
					// The count is only known once it is done: success says it.
					await errorToast(
						revokeOthers.mutateAsync().then((count) => {
							toast.success(
								t("account.sessions.signedOutOthers", { count })
							);
						})
					);
				}}
				title={t("account.sessions.signOutOthersTitle")}
				confirmText={t("account.sessions.signOutOthers")}
				confirmColor="danger"
			>
				{t("account.sessions.signOutOthersText")}
			</ConfirmModal>
		</SectionCard>
	);
};
